import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import logoImg from '../logo.jpg';

export const PrivacyPolicyPage: React.FC = () => {
    const navigate = useNavigate();
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

    const sections = [
        {
            title: "Information We Collect",
            paragraphs: [
                "To provide accurate analytics, reporting, and business intelligence features, Tvora may collect and process certain store-related information from your Shopify account. This information is used only to deliver the functionality of the application and improve your experience.",
                "The information we may collect includes:",
                "• Store information such as store name, domain, and Shopify account details\n• Order information including order values, fulfillment status, returns, and cancellations\n• Product information including inventory, pricing, collections, and product performance\n• Customer-related data such as customer purchase history and segmentation insights\n• Analytics and reporting data generated through the use of the application\n• Technical information such as browser type, device details, IP address, and usage logs",
                "We only request access to the information necessary for the platform to function properly and provide meaningful analytics and insights."
            ]
        },
        {
            title: "How We Use Your Information",
            paragraphs: [
                "The information collected through Tvora is primarily used to improve your store intelligence experience and help you make smarter business decisions.",
                "We may use your data for purposes such as:",
                "• Generating analytics dashboards and reports\n• Calculating sales, profit, and performance metrics\n• Providing customer segmentation and order intelligence\n• Monitoring inventory and product trends\n• Delivering forecasting and business insights\n• Improving app performance and reliability\n• Responding to support requests and technical issues\n• Enhancing user experience and application functionality",
                "We do not sell your store data or customer information to third parties."
            ]
        },
        {
            title: "Data Security",
            paragraphs: [
                "Protecting your data is extremely important to us. Tvora uses modern security practices and secure technologies to help protect information from unauthorized access, misuse, or disclosure.",
                "We implement reasonable technical and administrative safeguards designed to secure your data, including secure server infrastructure, encrypted communication methods, and controlled access systems.",
                "While we work hard to maintain a secure environment, no online platform or internet transmission can be guaranteed to be completely secure. Users should also take appropriate steps to protect their Shopify accounts and login credentials."
            ]
        },
        {
            title: "Shopify Integration",
            paragraphs: [
                "Tvora operates as a Shopify-integrated application. Certain permissions are required to access store data and provide the features available within the platform.",
                "The application only accesses information that has been authorized by the merchant during installation. Permissions may include access to orders, products, customers, analytics, and store-related operational data depending on the features being used.",
                "If you uninstall the application, future access to your Shopify store data will stop, although limited information may remain temporarily stored for legal, operational, or backup purposes."
            ]
        },
        {
            title: "Customer Information",
            paragraphs: [
                "Some features inside Tvora may process customer-related information to generate insights such as customer segmentation, repeat purchase behavior, lifetime value analysis, retention tracking, and customer intelligence reporting.",
                "This information is used solely for analytical and operational purposes within your store environment. Tvora does not use customer information for advertising or unrelated third-party marketing purposes.",
                "Store owners remain responsible for ensuring compliance with applicable privacy laws and customer data regulations related to their own business operations."
            ]
        },
        {
            title: "Cookies and Usage Tracking",
            paragraphs: [
                "Tvora may use cookies, local storage, and related technologies to improve functionality, maintain sessions, analyze platform usage, and enhance user experience.",
                "These technologies help us understand how users interact with the platform, identify performance issues, and optimize application features over time.",
                "Users can manage browser cookie settings through their device or browser preferences if they choose to limit certain tracking features."
            ]
        },
        {
            title: "Third-Party Services",
            paragraphs: [
                "In some cases, Tvora may rely on trusted third-party services or infrastructure providers to support hosting, analytics, communication, monitoring, or technical operations.",
                "These services are used only when necessary to operate and improve the platform efficiently. We work with providers that maintain appropriate security and privacy standards.",
                "We are not responsible for the privacy practices of third-party websites, platforms, or external services that may be linked from within the application."
            ]
        },
        {
            title: "Data Retention",
            paragraphs: [
                "We retain information only for as long as reasonably necessary to provide services, maintain system integrity, comply with legal obligations, resolve disputes, or improve platform functionality.",
                "If you uninstall Tvora or request account removal, we may delete or anonymize associated information within a reasonable timeframe unless retention is required for operational, legal, or security purposes."
            ]
        },
        {
            title: "User Rights and Control",
            paragraphs: [
                "We believe merchants should have transparency and control over their information. Depending on applicable laws and regulations, users may have rights related to:",
                "• Accessing stored information\n• Requesting correction of inaccurate data\n• Requesting deletion of certain information\n• Limiting specific types of processing\n• Contacting support regarding privacy concerns",
                "Requests related to privacy or data handling can be submitted through our support channels."
            ]
        },
        {
            title: "Children’s Privacy",
            paragraphs: [
                "Tvora: Analytics & Profit is intended for business and commercial use and is not designed for use by children. We do not knowingly collect personal information from individuals under the age required by applicable law."
            ]
        },
        {
            title: "Updates to This Privacy Policy",
            paragraphs: [
                "We may update this Privacy Policy from time to time to reflect platform improvements, legal requirements, or operational changes. Updated versions may be published within the application or website.",
                "We encourage users to review this policy periodically to stay informed about how information is handled and protected."
            ]
        },
        {
            title: "Contact Us",
            paragraphs: [
                "If you have questions, concerns, or requests related to this Privacy Policy or data handling practices, you may contact the Tvora support team through the available support channels provided inside the application or on the official website.",
                "At Tvora: Analytics & Profit, we are committed to building a secure, transparent, and trustworthy analytics platform for Shopify merchants. We appreciate the trust our users place in us and continuously work to maintain responsible privacy and data protection practices across the platform."
            ]
        }
    ];

    return (
        <div style={{
            minHeight: '100vh',
            fontFamily: "'Outfit', 'Inter', system-ui, -apple-system, sans-serif",
            color: '#0f172a',
            background: 'linear-gradient(135deg, #f8fafc 0%, #eff6ff 40%, #f5f3ff 70%, #faf5ff 100%)',
            overflowX: 'hidden',
            display: 'flex',
            flexDirection: 'column'
        }}>
            {/* Header */}
            <header className="landing-header" style={{
                position: 'sticky',
                top: 0,
                zIndex: 1000,
                background: 'rgba(255, 255, 255, 0.75)',
                backdropFilter: 'blur(20px)',
                WebkitBackdropFilter: 'blur(20px)',
                borderBottom: '1px solid rgba(241, 245, 249, 0.8)',
                padding: '1.25rem 2rem',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                boxShadow: '0 4px 30px rgba(0, 0, 0, 0.01)'
            }}>
                <div 
                    className="header-logo" 
                    onClick={() => navigate('/')}
                    style={{ display: 'flex', alignItems: 'center', gap: '0.85rem', cursor: 'pointer' }}
                >
                    <img 
                        src={logoImg} 
                        alt="Tvora Logo" 
                        style={{
                            width: '38px',
                            height: '38px',
                            borderRadius: '10px',
                            objectFit: 'cover',
                            boxShadow: '0 4px 20px rgba(99, 102, 241, 0.15)'
                        }} 
                    />
                    <div>
                        <span className="logo-title" style={{
                            fontWeight: 800,
                            fontSize: '1.45rem',
                            letterSpacing: '-0.03em',
                            background: 'linear-gradient(135deg, #1e293b 0%, #6366f1 100%)',
                            WebkitBackgroundClip: 'text',
                            backgroundClip: 'text',
                            WebkitTextFillColor: 'transparent'
                        }}>
                            Tvora
                        </span>
                        <span className="logo-subtitle" style={{
                            display: 'block',
                            fontSize: '0.65rem',
                            color: '#64748b',
                            fontWeight: 600,
                            letterSpacing: '0.08em',
                            textTransform: 'uppercase',
                            marginTop: '-2px'
                        }}>
                            Analytics & Profit
                        </span>
                    </div>
                </div>

                <nav style={{ display: 'flex', gap: '2rem', alignItems: 'center' }} className="nav-desktop">
                    <a href="/#features" onClick={() => navigate('/#features')} style={{ color: '#475569', textDecoration: 'none', fontWeight: 500, fontSize: '0.95rem', transition: 'color 0.2s' }}>Features</a>
                    <a href="/#showcase" onClick={() => navigate('/#showcase')} style={{ color: '#475569', textDecoration: 'none', fontWeight: 500, fontSize: '0.95rem', transition: 'color 0.2s' }}>Interactive Preview</a>
                    <a href="/#who-is-it-for" onClick={() => navigate('/#who-is-it-for')} style={{ color: '#475569', textDecoration: 'none', fontWeight: 500, fontSize: '0.95rem', transition: 'color 0.2s' }}>Target Audience</a>
                    <a href="/#pricing" onClick={() => navigate('/#pricing')} style={{ color: '#475569', textDecoration: 'none', fontWeight: 500, fontSize: '0.95rem', transition: 'color 0.2s' }}>Pricing</a>
                    <a href="/#faq" onClick={() => navigate('/#faq')} style={{ color: '#475569', textDecoration: 'none', fontWeight: 500, fontSize: '0.95rem', transition: 'color 0.2s' }}>FAQ</a>
                </nav>

                <div className="header-actions" style={{ display: 'flex', gap: '0.85rem', alignItems: 'center' }}>
                    <a href="/" onClick={() => navigate('/')} style={{
                        background: 'linear-gradient(135deg, #6366f1 0%, #3b82f6 100%)',
                        color: 'white',
                        padding: '0.65rem 1.35rem',
                        borderRadius: '12px',
                        textDecoration: 'none',
                        fontWeight: 600,
                        fontSize: '0.9rem',
                        boxShadow: '0 8px 25px rgba(99, 102, 241, 0.25)',
                        transition: 'all 0.2s'
                    }}>
                        Back to Home
                    </a>
                </div>

                {/* Hamburger Toggle Button */}
                <button
                    onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                    className="hamburger-button"
                    style={{
                        background: 'transparent',
                        border: 'none',
                        cursor: 'pointer',
                        padding: '0.5rem',
                        color: '#0f172a',
                        alignItems: 'center',
                        justifyContent: 'center'
                    }}
                >
                    {isMobileMenuOpen ? (
                        <svg style={{ width: '26px', height: '26px' }} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    ) : (
                        <svg style={{ width: '26px', height: '26px' }} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
                        </svg>
                    )}
                </button>
            </header>

            {/* Mobile Navigation Drawer */}
            <AnimatePresence>
                {isMobileMenuOpen && (
                    <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.25 }}
                        style={{
                            background: 'rgba(255, 255, 255, 0.98)',
                            backdropFilter: 'blur(20px)',
                            WebkitBackdropFilter: 'blur(20px)',
                            borderBottom: '1px solid rgba(226, 232, 240, 0.8)',
                            position: 'fixed',
                            top: '64px',
                            left: 0,
                            right: 0,
                            zIndex: 999,
                            overflow: 'hidden',
                            boxShadow: '0 15px 30px rgba(0, 0, 0, 0.05)'
                        }}
                        className="mobile-menu-drawer"
                    >
                        <div style={{
                            display: 'flex',
                            flexDirection: 'column',
                            padding: '1.5rem',
                            gap: '1.25rem'
                        }}>
                            <a href="/" onClick={(e) => { e.preventDefault(); setIsMobileMenuOpen(false); navigate('/'); }} style={{ color: '#0f172a', textDecoration: 'none', fontWeight: 700, fontSize: '1.05rem', padding: '0.25rem 0' }}>Home</a>
                            <a href="/#features" onClick={(e) => { e.preventDefault(); setIsMobileMenuOpen(false); navigate('/#features'); }} style={{ color: '#0f172a', textDecoration: 'none', fontWeight: 700, fontSize: '1.05rem', padding: '0.25rem 0' }}>Features</a>
                            <a href="/#showcase" onClick={(e) => { e.preventDefault(); setIsMobileMenuOpen(false); navigate('/#showcase'); }} style={{ color: '#0f172a', textDecoration: 'none', fontWeight: 700, fontSize: '1.05rem', padding: '0.25rem 0' }}>Interactive Preview</a>
                            <a href="/#who-is-it-for" onClick={(e) => { e.preventDefault(); setIsMobileMenuOpen(false); navigate('/#who-is-it-for'); }} style={{ color: '#0f172a', textDecoration: 'none', fontWeight: 700, fontSize: '1.05rem', padding: '0.25rem 0' }}>Target Audience</a>
                            <a href="/#pricing" onClick={(e) => { e.preventDefault(); setIsMobileMenuOpen(false); navigate('/#pricing'); }} style={{ color: '#0f172a', textDecoration: 'none', fontWeight: 700, fontSize: '1.05rem', padding: '0.25rem 0' }}>Pricing</a>
                            <a href="/#faq" onClick={(e) => { e.preventDefault(); setIsMobileMenuOpen(false); navigate('/#faq'); }} style={{ color: '#0f172a', textDecoration: 'none', fontWeight: 700, fontSize: '1.05rem', padding: '0.25rem 0' }}>FAQ</a>
                            <hr style={{ border: 'none', borderTop: '1px solid #f1f5f9', margin: '0.25rem 0' }} />
                            <a href="/support" onClick={(e) => { e.preventDefault(); setIsMobileMenuOpen(false); navigate('/support'); }} style={{ color: '#0f172a', textDecoration: 'none', fontWeight: 700, fontSize: '1.05rem', padding: '0.25rem 0' }}>Support Contact</a>
                            <a href="/privacy" onClick={(e) => { e.preventDefault(); setIsMobileMenuOpen(false); navigate('/privacy'); }} style={{ color: '#6366f1', textDecoration: 'none', fontWeight: 700, fontSize: '1.05rem', padding: '0.25rem 0' }}>Privacy Policy</a>
                            <a href="/terms" onClick={(e) => { e.preventDefault(); setIsMobileMenuOpen(false); navigate('/terms'); }} style={{ color: '#0f172a', textDecoration: 'none', fontWeight: 700, fontSize: '1.05rem', padding: '0.25rem 0' }}>Terms of Service</a>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Main Content */}
            <main style={{ flex: 1, padding: '4rem 2rem', maxWidth: '1000px', margin: '0 auto', width: '100%', boxSizing: 'border-box' }}>
                {/* Title Section */}
                <div style={{ textAlign: 'center', marginBottom: '3.5rem' }}>
                    <span style={{
                        fontSize: '0.82rem',
                        fontWeight: 700,
                        color: '#6366f1',
                        textTransform: 'uppercase',
                        letterSpacing: '0.12em',
                        display: 'block',
                        marginBottom: '0.75rem'
                    }}>
                        Legal Agreements
                    </span>
                    <h1 style={{
                        fontSize: '2.8rem',
                        fontWeight: 800,
                        letterSpacing: '-0.03em',
                        color: '#0f172a',
                        margin: 0,
                        lineHeight: 1.15
                    }}>
                        Privacy Policy
                    </h1>
                    <p style={{
                        fontSize: '1.2rem',
                        color: '#64748b',
                        marginTop: '0.8rem',
                        fontWeight: 500
                    }}>
                        Tvora: Analytics & Profit Data Privacy Policy
                    </p>
                </div>

                {/* Introductory Card */}
                <div className="glass-card" style={{
                    padding: '2.5rem',
                    background: 'rgba(255, 255, 255, 0.65)',
                    backdropFilter: 'blur(20px)',
                    WebkitBackdropFilter: 'blur(20px)',
                    border: '1px solid rgba(255, 255, 255, 0.6)',
                    borderRadius: '24px',
                    boxShadow: '0 10px 40px rgba(0, 0, 0, 0.02)',
                    lineHeight: '1.7',
                    marginBottom: '2.5rem'
                }}>
                    <p style={{ margin: '0 0 1.25rem 0', fontSize: '1.05rem', color: '#334155' }}>
                        At <strong>Tvora: Analytics & Profit</strong>, we value your privacy and understand how important your business data is. This Privacy Policy explains how we collect, use, store, and protect information when you use our platform, website, and Shopify application. Our goal is to maintain transparency and ensure that every merchant using Tvora feels confident and secure while accessing our analytics and intelligence tools.
                    </p>
                    <p style={{ margin: '0', fontSize: '1.05rem', color: '#334155' }}>
                        By installing or using Tvora: Analytics & Profit, you agree to the practices described in this Privacy Policy. We encourage all users to read this document carefully to understand how their information is handled.
                    </p>
                </div>

                {/* Document Sections */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>
                    {sections.map((section, idx) => (
                        <div key={idx} className="glass-card" style={{
                            padding: '2.25rem',
                            background: 'rgba(255, 255, 255, 0.65)',
                            backdropFilter: 'blur(20px)',
                            WebkitBackdropFilter: 'blur(20px)',
                            border: '1px solid rgba(255, 255, 255, 0.6)',
                            borderRadius: '24px',
                            boxShadow: '0 10px 40px rgba(0, 0, 0, 0.02)'
                        }}>
                            <h3 style={{ fontSize: '1.35rem', fontWeight: 800, color: '#0f172a', margin: '0 0 1.25rem 0' }}>
                                {section.title}
                            </h3>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', lineHeight: '1.65' }}>
                                {section.paragraphs.map((p, pIdx) => {
                                    if (p.includes('\n')) {
                                        return (
                                            <div key={pIdx} style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem', paddingLeft: '0.5rem', marginTop: '0.5rem', marginBottom: '0.5rem' }}>
                                                {p.split('\n').map((li, liIdx) => (
                                                    <div key={liIdx} style={{ display: 'flex', gap: '0.5rem', color: '#334155', fontSize: '1rem', fontWeight: 500 }}>
                                                        <span style={{ color: '#6366f1' }}>•</span>
                                                        <span>{li.replace('• ', '')}</span>
                                                    </div>
                                                ))}
                                            </div>
                                        );
                                    }
                                    return (
                                        <p key={pIdx} style={{ margin: 0, fontSize: '1rem', color: '#475569' }}>
                                            {p}
                                        </p>
                                    );
                                })}
                            </div>
                        </div>
                    ))}
                </div>
            </main>

            {/* Footer */}
            <footer style={{
                background: '#0f172a',
                color: '#94a3b8',
                padding: '4rem 2rem 2.5rem',
                borderTop: '1px solid rgba(255, 255, 255, 0.05)',
                marginTop: '4rem'
            }}>
                <div style={{ maxWidth: '1200px', margin: '0 auto', display: 'flex', flexDirection: 'column', gap: '3rem' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '3rem' }}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', maxWidth: '320px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', cursor: 'pointer' }} onClick={() => navigate('/')}>
                                <img src={logoImg} alt="Tvora" style={{ width: '32px', height: '32px', borderRadius: '8px' }} />
                                <span style={{ fontWeight: 800, fontSize: '1.25rem', color: 'white', letterSpacing: '-0.02em' }}>Tvora</span>
                            </div>
                            <p style={{ fontSize: '0.85rem', lineHeight: 1.6, margin: 0 }}>
                                Advanced business intelligence, real-time sync, customer retention modeling, and net profit calculations for growth-minded Shopify store owners.
                            </p>
                        </div>

                        <div style={{ display: 'flex', gap: '4rem', flexWrap: 'wrap' }}>
                            <div>
                                <h5 style={{ color: 'white', margin: '0 0 1rem 0', fontSize: '0.9rem', fontWeight: 700 }}>Product</h5>
                                <ul style={{ padding: 0, margin: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: '0.65rem', fontSize: '0.85rem' }}>
                                    <li><a href="/#features" onClick={() => navigate('/#features')} style={{ color: '#64748b', textDecoration: 'none' }}>Features</a></li>
                                    <li><a href="/#showcase" onClick={() => navigate('/#showcase')} style={{ color: '#64748b', textDecoration: 'none' }}>Dashboard Showcase</a></li>
                                    <li><a href="/#pricing" onClick={() => navigate('/#pricing')} style={{ color: '#64748b', textDecoration: 'none' }}>Pricing Plans</a></li>
                                </ul>
                            </div>
                            <div>
                                <h5 style={{ color: 'white', margin: '0 0 1rem 0', fontSize: '0.9rem', fontWeight: 700 }}>Resources</h5>
                                <ul style={{ padding: 0, margin: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: '0.65rem', fontSize: '0.85rem' }}>
                                    <li><a href="/#faq" onClick={() => navigate('/#faq')} style={{ color: '#64748b', textDecoration: 'none' }}>FAQ</a></li>
                                    <li><a href="/support" onClick={() => navigate('/support')} style={{ color: '#64748b', textDecoration: 'none' }}>Support Contact</a></li>
                                    <li><a href="/privacy" onClick={() => navigate('/privacy')} style={{ color: '#6366f1', textDecoration: 'none', fontWeight: 600 }}>Privacy Policy</a></li>
                                    <li><a href="/terms" onClick={() => navigate('/terms')} style={{ color: '#64748b', textDecoration: 'none' }}>Terms of Service</a></li>
                                </ul>
                            </div>
                        </div>
                    </div>

                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem', borderTop: '1px solid rgba(255,255,255,0.05)', paddingTop: '2rem', fontSize: '0.82rem' }}>
                        <span>© {new Date().getFullYear()} Tvora: Analytics & Profit. All rights reserved.</span>
                        <span>Need assistance? Contact <a href="mailto:support@tvora.co" style={{ color: '#6366f1', textDecoration: 'none', fontWeight: 600 }}>support@tvora.co</a></span>
                    </div>
                </div>
            </footer>

            {/* Mobile/Desktop styling overrides */}
            <style dangerouslySetInnerHTML={{__html: `
                .hamburger-button {
                    display: none !important;
                }
                @media (max-width: 768px) {
                    main {
                        padding: 2.5rem 1.25rem !important;
                    }
                    h1 {
                        font-size: 2rem !important;
                    }
                    .glass-card {
                        padding: 1.5rem !important;
                    }
                    .nav-desktop, .header-actions {
                        display: none !important;
                    }
                    .landing-header .hamburger-button {
                        display: flex !important;
                        margin-left: auto !important;
                    }
                    .mobile-menu-drawer {
                        top: 64px !important;
                    }
                }
            `}} />
        </div>
    );
};
